const RU_MONTHS_SHORT = [
  'янв', 'фев', 'мар', 'апр', 'мая', 'июн',
  'июл', 'авг', 'сен', 'окт', 'ноя', 'дек',
];

const RU_MONTHS_LONG = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function parseDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  // Date-only strings (drawn_at, day, started_at, ...) are calendar dates, not instants —
  // parse as local midnight. `new Date('YYYY-MM-DD')` per spec is UTC midnight, which
  // formatDate's local getDate()/getMonth()/getFullYear() then shifts back a day in any
  // negative-UTC-offset zone. Full timestamps (with time and/or a zone designator) keep
  // being parsed as-is, so their UTC→local conversion in formatDate stays correct.
  const d = DATE_ONLY_RE.test(iso) ? new Date(`${iso}T00:00:00`) : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(iso: string | null | undefined, style: 'short' | 'long' = 'short'): string {
  const d = parseDate(iso);
  if (!d) return '—';
  const day = d.getDate();
  const monthIdx = d.getMonth();
  const year = d.getFullYear();
  const month = (style === 'long' ? RU_MONTHS_LONG : RU_MONTHS_SHORT)[monthIdx];
  return `${day} ${month} ${year}`;
}

export function formatRelative(iso: string | null | undefined): string {
  const d = parseDate(iso);
  if (!d) return '—';
  const deltaMs = Date.now() - d.getTime();
  const min = Math.floor(deltaMs / 60_000);
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин назад`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours} ч назад`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} дн назад`;
  return formatDate(iso);
}

export function formatNumber(v: number | null | undefined, fractionDigits = 2): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const digits = Math.abs(v) >= 100 ? Math.min(1, fractionDigits) : fractionDigits;
  const s = v.toFixed(digits);
  if (!s.includes('.')) return s;
  return s.replace(/0+$/, '').replace(/\.$/, '');
}

/**
 * Значение лабораторного маркёра: столько знаков, чтобы соседние сдачи не
 * слиплись в одно число.
 *
 * `formatNumber` округляет до двух знаков, и на большинстве показателей этого
 * хватает. Но не на всех: относительная плотность мочи 1.006 и 1.019
 * превращаются в «1.01» и «1.02» — две сдачи выглядят почти одинаково, хотя
 * различаются в третьем знаке, который тут и есть весь смысл. У микроэлементов
 * хуже: марганец 0.0017 при двух знаках это «0», то есть значение исчезает.
 *
 * Правило — не меньше трёх значащих цифр, а дальше как обычно: хвостовые нули
 * срезаются, так что 14.8 остаётся «14.8», а не становится «14.800».
 */
export function formatLabValue(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  if (abs === 0) return '0';
  // Хвост (toFixed + срез нулей + кламп сотен) целиком в formatNumber — здесь
  // только выбор точности, иначе две копии одного форматирования разъедутся.
  if (abs >= 1) return formatNumber(v, 3);
  // Верхняя граница 8 знаков: дальше toFixed уходит в экспоненту, а показатель
  // мельче 1e-6 в этом справочнике не встречается.
  const order = Math.floor(Math.log10(abs));
  let precision = Math.min(8, 2 - order);
  // Три значащие цифры у значения под самой границей десятки перебрасывают его
  // ЧЕРЕЗ границу: 0.9999 печаталось как «1», то есть ровно порог, ниже которого
  // значение на самом деле лежит (у МНО порог именно единица). Добавляем знаки,
  // пока граница не съедается; на потолке в 8 знаков останавливаемся.
  const boundary = 10 ** (order + 1);
  while (precision < 8 && Number(abs.toFixed(precision)) >= boundary) precision += 1;
  return formatNumber(v, precision);
}

export function formatDelta(
  v: number | null | undefined,
  format: (x: number | null | undefined) => string = formatNumber,
): string {
  if (v == null || !Number.isFinite(v)) return '';
  const sign = v > 0 ? '+' : '';
  return `${sign}${format(v)}`;
}

/**
 * Референсный интервал для таблиц. Модель отдаёт односторонние границы как одну
 * заполненную границу ("< 8.6" → low=null/high=8.6, "> 1.0" → low=1.0/high=null),
 * поэтому показываем все четыре случая — иначе односторонние диапазоны схлопываются в "—".
 */
export function formatRef(low: number | null | undefined, high: number | null | undefined): string {
  const hasLow = low != null && Number.isFinite(low);
  const hasHigh = high != null && Number.isFinite(high);
  if (hasLow && hasHigh) return `${formatLabValue(low)}–${formatLabValue(high)}`;
  if (hasHigh) return `< ${formatLabValue(high)}`;
  if (hasLow) return `> ${formatLabValue(low)}`;
  return '—';
}

export interface RefCell {
  text: string;
  /** true → значение взято из дефолта каталога, а не из отчёта лаборатории (показывать приглушённо). */
  fromCatalog: boolean;
}

/**
 * Референс для колонки/строки с фолбэком на дефолт каталога. Приоритет:
 * per-result диапазон (тот, по которому лаборатория ставила флаг) → канонический
 * дефолт маркёра из каталога (когда лаба дала "см.комм"/прочерк) → "—".
 */
export function refWithFallback(
  low: number | null | undefined,
  high: number | null | undefined,
  defLow: number | null | undefined,
  defHigh: number | null | undefined,
): RefCell {
  const has = (v: number | null | undefined): boolean => v != null && Number.isFinite(v);
  if (has(low) || has(high)) return { text: formatRef(low, high), fromCatalog: false };
  if (has(defLow) || has(defHigh)) return { text: formatRef(defLow, defHigh), fromCatalog: true };
  return { text: '—', fromCatalog: false };
}

export function deltaDirection(v: number | null | undefined): 'up' | 'down' | 'flat' | null {
  if (v == null || !Number.isFinite(v)) return null;
  if (Math.abs(v) < 1e-9) return 'flat';
  return v > 0 ? 'up' : 'down';
}

export function flagToStatus(flag: string | null | undefined): 'high' | 'low' | 'norm' | 'unknown' {
  if (!flag) return 'norm';
  if (flag === 'H' || flag === 'HH') return 'high';
  if (flag === 'L' || flag === 'LL') return 'low';
  if (flag === 'N') return 'norm';
  return 'unknown';
}

export function statusLabel(status: 'high' | 'low' | 'norm' | 'borderline' | 'unknown' | 'stale'): string {
  switch (status) {
    case 'high': return 'Выше нормы';
    case 'low': return 'Ниже нормы';
    case 'norm': return 'Норма';
    case 'borderline': return 'Пограничное';
    case 'unknown': return 'Неизвестно';
    case 'stale': return 'Устаревший';
  }
}

export function plural(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
}

export function daysSince(iso: string | null | undefined): number | null {
  const d = parseDate(iso);
  if (!d) return null;
  return Math.floor((Date.now() - d.getTime()) / 86_400_000);
}

export type WindowKey = '7d' | '30d' | '90d';

export const WINDOW_LABELS: Record<WindowKey, string> = {
  '7d': '7 дней',
  '30d': '30 дней',
  '90d': '90 дней',
};

export function windowDays(w: WindowKey): 7 | 30 | 90 {
  return w === '7d' ? 7 : w === '30d' ? 30 : 90;
}

/** Tail-slice to the last `windowDays(window)` items (all if fewer). */
export function sliceRecent<T>(items: T[], window: WindowKey): T[] {
  const days = windowDays(window);
  return items.length <= days ? items : items.slice(items.length - days);
}

export function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}
